import React, { useState, useEffect, useRef, useMemo } from "react";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import MDTypography from "components/MDTypography";
import reportsBarChartData from "layouts/dashboard/data/reportsBarChartData";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";
import Projects from "layouts/dashboard/components/Projects";
import OrdersOverview from "layouts/dashboard/components/OrdersOverview";
import nodata from "assets/images/nodata.png";
import { Dropdown } from "semantic-ui-react";
import { TextField, InputAdornment, FormControl, InputLabel, Select } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MDProgress from "components/MDProgress";
import DataTable from "examples/ReportTable/index";
import "semantic-ui-css/semantic.min.css";
import "./dashboard.css";
import typography from "assets/theme/base/typography";

// Custom Searchable Dropdown Component
const SearchableDropdown = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Select...", 
  disabled = false,
  valueKey = "id",
  labelKey = "name",
  className = "form-control form-control-md"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setFilteredOptions(
      options.filter(option =>
        option[labelKey]?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, options, labelKey]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option[valueKey]);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  };

  const getDisplayValue = () => {
    if (!value) return "";
    const selectedOption = options.find(opt => opt[valueKey] === value);
    return selectedOption ? selectedOption[labelKey] : "";
  };

  return (
    <div className="searchable-dropdown" ref={dropdownRef} style={{ position: 'relative' }}>
      <div
        className={`${className} ${disabled ? 'disabled' : ''}`}
        onClick={handleInputClick}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: disabled ? '#f5f5f5' : 'white',
          opacity: disabled ? 0.6 : 1
        }}
      >
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={`Search ${placeholder.toLowerCase()}...`}
            style={{
              border: 'none',
              outline: 'none',
              background: 'transparent',
              width: '100%',
              fontSize: 'inherit'
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsOpen(false);
                setSearchTerm("");
              }
            }}
          />
        ) : (
          <span style={{ color: value ? 'inherit' : '#999' }}>
            {getDisplayValue() || placeholder}
          </span>
        )}
        <span style={{ marginLeft: '8px' }}>
          {isOpen ? '▲' : '▼'}
        </span>
      </div>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderTop: 'none',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          {/* Clear/All option */}
          <div
            onClick={() => handleSelect({ [valueKey]: "", [labelKey]: "" })}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              borderBottom: '1px solid #eee',
              backgroundColor: !value ? '#f0f0f0' : 'transparent'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
            onMouseLeave={(e) => e.target.style.backgroundColor = !value ? '#f0f0f0' : 'transparent'}
          >
            All {placeholder.replace('Select ', '').replace('All ', '')}
          </div>

          {filteredOptions.length === 0 ? (
            <div style={{ padding: '8px 12px', color: '#999', fontStyle: 'italic' }}>
              No options found
            </div>
          ) : (
            filteredOptions.map((option, index) => (
              <div
                key={index}
                onClick={() => handleSelect(option)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  backgroundColor: option[valueKey] === value ? '#e3f2fd' : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (option[valueKey] !== value) {
                    e.target.style.backgroundColor = '#f0f0f0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (option[valueKey] !== value) {
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {option[labelKey]}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

function Dashboard() {
  const navigate = useNavigate();
  const { sales, tasks } = reportsLineChartData;
  const userDepartment = JSON.parse(sessionStorage.getItem("department_id"))
  var user = JSON.parse(sessionStorage.getItem("user"));
  if (user) {
    var organization_id = user.organization_id;
  } else {
    var organization_id = 1;
  }
  const [studentsEnrolled, setStudentsEnrolled] = useState(0);
  const [totalApplications, setTotalApplications] = useState(0);
  const [completedApplications, setCompletedApplications] = useState(0);
  const [successfulAdmissions, setSuccessfulAdmissions] = useState(0);
  const [totalPaidStudents, setTotalPaidStudents] = useState(0);
  const [totalUnpaid, setTotalUnpaid] = useState(0);
  const [totalUnverifiedStudents, setTotalUnverifiedStudents] = useState(0);
  const [totalVerifiedStudents, setTotalVerifiedStudents] = useState(0);
  const [totalProcessingPaidVouchers, setTotalProcessingPaidVouchers] = useState(0);
  const [departmentReport, setDepartmentReport] = useState([]);
  const [topProgramsReport, setTopProgramsReport] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState("");

  const [provincesList, setProvincesList] = useState([]);
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedReligion, setSelectedReligion] = useState("");
  const [selectedNationality, setSelectedNationality] = useState("");
  const [isCampaignSelected, setIsCampaignSelected] = useState(false);
  const [degree_id, setDegreeId] = useState();
  const [all_degrees, setAllDegrees] = useState([]);
  const [all_departments, setAllDepartments] = useState([]);
  const [department_id, setDepartmentId] = useState(
    userDepartment != null ? userDepartment : undefined
  );
  const [all_campaigns, setAllCampaigns] = useState([]);
  const allCampaignsRef = useRef([]);
  const [campaign_id, setCampaignId] = useState();
  const campaign_id_ref = useRef();
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState([null, null]);

  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('department_asc');

  const selectedCampaignName = useMemo(() => {
    const campaign = all_campaigns.find((c) => c.id === campaign_id);
    return campaign?.name || "";
  }, [all_campaigns, campaign_id]);

  const displayDepartments = useMemo(() => {
    const list = departmentSearchTerm
      ? departmentReport.filter((dept) =>
          dept.department_name?.toLowerCase().includes(departmentSearchTerm.toLowerCase())
        )
      : [...departmentReport];

    const sorted = [...list];
    switch (sortOption) {
      case 'department_desc':
        sorted.sort((a, b) =>
          (b.department_name || '').localeCompare(a.department_name || '')
        );
        break;
      case 'applications_desc':
        sorted.sort(
          (a, b) => (b.total_applications || 0) - (a.total_applications || 0)
        );
        break;
      case 'applications_asc':
        sorted.sort(
          (a, b) => (a.total_applications || 0) - (b.total_applications || 0)
        );
        break;
      case 'department_asc':
      default:
        sorted.sort((a, b) =>
          (a.department_name || '').localeCompare(b.department_name || '')
        );
        break;
    }
    return sorted;
  }, [departmentReport, departmentSearchTerm, sortOption]);

  // Define static options for gender, religion, and nationality
  const genderOptions = [
    { id: "male", name: "Male" },
    { id: "female", name: "Female" },
    { id: "other", name: "Other" }
  ];

  const religionOptions = [
    { id: "Muslim", name: "Muslim" },
    { id: "Non-Muslim", name: "Non-Muslim" }
  ];

  const nationalityOptions = [
    { id: "Pakistani", name: "Pakistani" },
    { id: "Overseas", name: "Overseas" },
    { id: "Other", name: "Other" }
  ];

  const getAllCampaigns = async () => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    await fetch(BASE_URL + `/campaign/get_campaigns`, requestOptions)
      .then((response) => response.json())
      .then((data) => {
        const sortedCampaigns = [...(data.campaigns || [])].sort((a, b) => {
          const aTime = new Date(a.createdAt || 0).getTime();
          const bTime = new Date(b.createdAt || 0).getTime();
          if (bTime !== aTime) return bTime - aTime;
          return (b.id || 0) - (a.id || 0);
        });
        setAllCampaigns(sortedCampaigns);
        allCampaignsRef.current = sortedCampaigns;
        const activeCampaign = sortedCampaigns.find((camp) => camp.status == 1);
        if (activeCampaign) {
          setCampaignId(activeCampaign.id);
          campaign_id_ref.current = activeCampaign.id;
          getDepartments(activeCampaign.id);
          setFiltersVisible(true);
          setIsCampaignSelected(true);
          fetchDepartmentReport(activeCampaign.id);
          const topFilters =
            userDepartment != null ? { department_id: userDepartment } : {};
          fetchTopProgramsReport(activeCampaign.id, topFilters);
        } else {
          setIsCampaignSelected(false);
          setDepartmentReport([]);
          setTopProgramsReport([]);
        }
      })
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const response = await fetch(`${BASE_URL}/submit_application/get_states`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (data && data.states) {
          setProvincesList(data.states);
        }
      } catch (error) {
        console.error("Error fetching provinces:", error);
      }
    };

    fetchProvinces();
  }, [BASE_URL, token]);

  const getDepartments = async (campaign_id) => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    await fetch(BASE_URL + `/department/get_departments_by_campaign/${campaign_id}`, requestOptions)
      .then((response) => response.json())
      .then((data) => {
        setAllDepartments(data.departments);
        if (userDepartment != null) {
          const deptId = userDepartment;
          const matched = (data.departments || []).find((d) => d.id == deptId);
          const resolvedDeptId = matched ? matched.id : deptId;
          setDepartmentId(resolvedDeptId);
          getPrograms(resolvedDeptId);
        }
      })
      .catch((error) => console.log(error));
  };

  const getPrograms = async (department_id) => {
    const activeCampaign = campaign_id_ref.current
    let url = '';
    if (!activeCampaign) {
      url = BASE_URL + `/degree/get_degrees_by_department/${department_id}`
    }
    else {
      url = BASE_URL + `/degree/get_degrees_by_department/${department_id}?campaign_id=${activeCampaign}`;
    }
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    await fetch(url, requestOptions)
      .then((response) => response.json())
      .then((data) => {
        setAllDegrees(data.degrees);
      })
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    getAllCampaigns();
  }, []);

  const handleCampaign = (value) => {
    campaign_id_ref.current = value;
    setCampaignId(value);
    if (value) {
      getDepartments(value);
      setFiltersVisible(true);
      setIsCampaignSelected(true);
      fetchDepartmentReport(value);
      const topFilters =
        userDepartment != null
          ? { department_id: department_id || userDepartment }
          : {};
      fetchTopProgramsReport(value, topFilters);
    } else {
      setFiltersVisible(false);
      setIsCampaignSelected(false);
      setDepartmentReport([]);
      setTopProgramsReport([]);
    }
  };

  const fetchDashboardDataWithFilters = async () => {
    const filters = {
      department_id,
      program_id: degree_id,
      province: selectedProvince,
      gender: selectedGender,
      religion: selectedReligion,
      nationality: selectedNationality,
    };

    const queryString = new URLSearchParams(
      Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {})
    ).toString();

    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(
        `${BASE_URL}/application/get_dashboard_data/${campaign_id}?${queryString}`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      setTotalApplications(data.total_applications);
      setCompletedApplications(data.completed_applications);
      setSuccessfulAdmissions(data.successfull_admission_granted);
      setTotalPaidStudents(data.total_paid_students);
      setTotalUnpaid(data.total_unpaid_students);
      setTotalUnverifiedStudents(data.total_unverified_applications);
      setTotalVerifiedStudents(data.total_verified_students);
      setStudentsEnrolled(data.total_applications_as_first_preference);
      setTotalProcessingPaidVouchers(data.total_processing_paid_vouchers);
    } catch (error) {
      console.error("Error fetching dashboard data with filters:", error);
    }
  };

  const fetchDepartmentReport = async (campaign) => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(
        `${BASE_URL}/application/campaign_department_report/${campaign}`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      setDepartmentReport(data.report);
    } catch (error) {
      console.error("Error fetching department report:", error);
    }
  };

  const topProgramsBarColors = [
    "#e91e63", "#1A73E8", "#4CAF50", "#fb8c00", "#9c27b0",
    "#00bcd4", "#ff5722", "#3f51b5", "#8bc34a", "#ffc107",
    "#673ab7", "#009688", "#f44336", "#2196f3", "#cddc39",
    "#ff9800", "#795548", "#607d8b", "#ec407a", "#00acc1",
    "#7cb342", "#5c6bc0", "#ef5350", "#26a69a", "#ab47bc",
  ];

  const formatTopProgramLabel = (item) => {
    const programName = item.program_name || "Unknown";
    const departmentName = (item.department_name || "").trim();
    if (!departmentName || departmentName === "N/A") {
      return programName;
    }
    return `${programName} (${departmentName})`;
  };

  const topProgramsChartData = useMemo(() => ({
    labels: topProgramsReport.map((item) => formatTopProgramLabel(item)),
    datasets: [
      {
        label: "Applications",
        color: "info",
        backgroundColor: topProgramsReport.map(
          (_, index) => topProgramsBarColors[index % topProgramsBarColors.length]
        ),
        data: topProgramsReport.map((item) => item.total_applications || 0),
      },
    ],
  }), [topProgramsReport]);

  const topProgramsChartHeight = useMemo(() => {
    const count = topProgramsReport.length || 1;
    const longestLabel = Math.max(
      ...topProgramsReport.map((item) => formatTopProgramLabel(item).length),
      1
    );
    const rowHeight = longestLabel > 50 ? 72 : longestLabel > 35 ? 64 : 56;
    return Math.max(420, count * rowHeight);
  }, [topProgramsReport]);

  const topProgramsYAxisLabelsPlugin = useMemo(
    () => ({
      id: "topProgramsYAxisLabels",
      afterDraw(chart) {
        const { ctx, chartArea, scales } = chart;
        const yScale = scales.y;

        if (!yScale || !topProgramsReport.length) return;

        const fontFamily = typography.fontFamily;
        const labelFont = `bold 14px ${fontFamily}`;
        ctx.save();
        ctx.textAlign = "right";
        ctx.textBaseline = "middle";

        topProgramsReport.forEach((item, index) => {
          const y = yScale.getPixelForValue(index);
          const programName = formatTopProgramLabel(item);
          const count = item.total_applications || 0;
          const labelX = chartArea.left - 10;

          ctx.fillStyle = "#000000";
          ctx.font = labelFont;
          ctx.fillText(programName, labelX, y - 10);

          ctx.fillText(`(${count})`, labelX, y + 10);
        });

        ctx.restore();
      },
    }),
    [topProgramsReport]
  );

  const topProgramsChartOptions = useMemo(() => {
    const longestLabel = Math.max(
      ...topProgramsReport.map((item) => formatTopProgramLabel(item).length),
      1
    );

    return {
      layout: {
        padding: {
          left: 0,
          right: 12,
        },
      },
      scales: {
        y: {
          position: "left",
          afterFit(axis) {
            axis.width = Math.max(axis.width, longestLabel * 7);
          },
          ticks: {
            display: false,
            autoSkip: false,
          },
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            title: (items) => items[0]?.label || "",
            label: (context) => `Applications: ${context.parsed.x}`,
          },
        },
      },
    };
  }, [topProgramsReport]);

  const fetchTopProgramsReport = async (campaign, appliedFilters = {}) => {
    const queryString = new URLSearchParams(
      Object.entries(appliedFilters).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {})
    ).toString();

    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(
        `${BASE_URL}/application/campaign_top_programs_report/${campaign}${queryString ? `?${queryString}` : ""}`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      setTopProgramsReport(data.report || []);
    } catch (error) {
      console.error("Error fetching top programs report:", error);
      setTopProgramsReport([]);
    }
  };

  useEffect(() => {
    if(userDepartment == null){
      if (campaign_id) {
        fetchDashboardDataWithFilters();
      }
    } else {
      if (campaign_id && department_id) {
        fetchDashboardDataWithFilters();
      }
    }
  }, [
    campaign_id,
    department_id,
    degree_id,
    selectedProvince,
    selectedGender,
    selectedReligion,
    selectedNationality,
  ]);

  useEffect(() => {
    if (!campaign_id) {
      setTopProgramsReport([]);
      return;
    }

    // Keep chart API filters aligned with dashboard cards.
    const filters = {
      department_id,
      program_id: degree_id,
      province: selectedProvince,
      gender: selectedGender,
      religion: selectedReligion,
      nationality: selectedNationality,
    };

    fetchTopProgramsReport(campaign_id, filters);
  }, [
    campaign_id,
    department_id,
    degree_id,
    selectedProvince,
    selectedGender,
    selectedReligion,
    selectedNationality,
  ]);

  const handleDepartment = (value) => {
    setDepartmentId(value);
    getPrograms(value);
  };

  const handleProgram = (value) => {
    setDegreeId(value);
  };

  const handleDateChange = (dates) => {
    setSelectedDateRange(dates);
  };

  const handleViewAllPrograms = () => {
    if (!campaign_id) return;

    const params = new URLSearchParams(
      Object.entries({
        campaign_id,
        campaign_name: selectedCampaignName,
        department_id,
        program_id: degree_id,
        province: selectedProvince,
        gender: selectedGender,
        religion: selectedReligion,
        nationality: selectedNationality,
      }).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {})
    ).toString();

    navigate(`/all_programs_applications_report${params ? `?${params}` : ""}`);
  };

  const columns = [
    { Header: "Department", accessor: "departments", align: "center" },
    {
      Header: "No. of Applicantions",
      accessor: "applications",
      align: "center",
    },
    {
      Header: "Completion Percentage",
      accessor: "completion",
      align: "center",
    },
  ];

  const rows = displayDepartments.map((item) => ({
    departments: item.department_name,
    applications: item.total_applications,
    completion: (
      <MDBox width="10rem">
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <MDTypography variant="caption" color="text">
            {item.completed_percentage ? `${item.completed_percentage.toFixed(2)}%` : '0.00%'}
          </MDTypography>
        </MDBox>
        <MDProgress
          value={item.completed_percentage}
          color="info"
          variant="gradient"
          label={false}
        />
      </MDBox>
    ),
  }));

  const [menu, setMenu] = useState(null);

  const openMenu = ({ currentTarget }) => setMenu(currentTarget);
  const closeMenu = () => setMenu(null);

  const renderMenu = (
    <Menu
      id="simple-menu"
      anchorEl={menu}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(menu)}
      onClose={closeMenu}
    >
      <MenuItem onClick={closeMenu}>Action</MenuItem>
      <MenuItem onClick={closeMenu}>Another action</MenuItem>
      <MenuItem onClick={closeMenu}>Something else</MenuItem>
    </Menu>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="p-4">
        <label htmlFor="campaign" className="block mb-2">
          Select Campaign
        </label>
        <SearchableDropdown
          options={all_campaigns}
          value={campaign_id}
          onChange={handleCampaign}
          placeholder="Select Campaign"
          valueKey="id"
          labelKey="name"
        />

        <Grid item xs={12}>
          <div className="d-flex align-items-end justify-content-between">
            {filtersVisible && (
              <div className="d-flex flex-wrap gap-4 mt-4">
                <div className="filter-dropdown">
                  <SearchableDropdown
                    options={all_departments}
                    value={department_id}
                    onChange={handleDepartment}
                    placeholder="All Departments"
                    disabled={userDepartment ? true : false}
                    valueKey="id"
                    labelKey="name"
                  />
                </div>

                <div className="filter-dropdown">
                  <SearchableDropdown
                    options={all_degrees}
                    value={degree_id}
                    onChange={handleProgram}
                    placeholder="All Programs"
                    valueKey="id"
                    labelKey="name"
                  />
                </div>

                <div className="filter-dropdown">
                  <SearchableDropdown
                    options={provincesList}
                    value={selectedProvince}
                    onChange={setSelectedProvince}
                    placeholder="All Provinces"
                    valueKey="name"
                    labelKey="name"
                  />
                </div>

                <div className="filter-dropdown">
                  <SearchableDropdown
                    options={genderOptions}
                    value={selectedGender}
                    onChange={setSelectedGender}
                    placeholder="All Genders"
                    valueKey="id"
                    labelKey="name"
                  />
                </div>

                <div className="filter-dropdown">
                  <SearchableDropdown
                    options={religionOptions}
                    value={selectedReligion}
                    onChange={setSelectedReligion}
                    placeholder="All Religions"
                    valueKey="id"
                    labelKey="name"
                  />
                </div>

                <div className="filter-dropdown">
                  <SearchableDropdown
                    options={nationalityOptions}
                    value={selectedNationality}
                    onChange={setSelectedNationality}
                    placeholder="All Nationalities"
                    valueKey="id"
                    labelKey="name"
                  />
                </div>
              </div>
            )}
          </div>
        </Grid>
      </div>
      <MDBox py={3} mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="Total"
                title="Total Applications"
                count={totalApplications}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon="thumb_up"
                color="success"
                title="Completed Applicantions"
                count={completedApplications}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon="paid"
                color="primary"
                title="Processing Paid Vouchers"
                count={totalProcessingPaidVouchers}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="person_add"
                title="Total Admissions"
                count={successfulAdmissions}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="primary"
                icon="paid"
                title="Total Paid Students"
                count={totalPaidStudents}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="light"
                icon="money_off"
                title="Total Unpaid"
                count={totalUnpaid}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="warning"
                icon="person_off"
                title="Total Unverified students"
                count={totalUnverifiedStudents}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="info"
                icon="verified"
                title="Total Verified students"
                count={totalVerifiedStudents}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            {degree_id && (
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="dark"
                  icon="school"
                  title="Total Applications as first Preference"
                  count={studentsEnrolled}
                />
              </MDBox>
            )}
          </Grid>
        </Grid>
        <MDBox mt={4.5}>
        </MDBox>
        {userDepartment == null && (
        <MDBox>
          <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Card className="dashboard-table">
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
              >
                <MDBox>
                  <MDTypography variant="h6" gutterBottom>
                    Current Campaign Report
                  </MDTypography>
                  <style>{`
                    .campaign-report-toolbar {
                      display: flex;
                      flex-direction: column;
                      gap: 12px;
                      margin-top: 10px;
                    }

                    @media (min-width: 600px) {
                      .campaign-report-toolbar {
                        flex-direction: row;
                        align-items: center;
                      }
                    }

                    .campaign-report-search {
                      flex: 1;
                      min-width: 0;
                    }

                    .campaign-report-search .MuiOutlinedInput-root,
                    .campaign-report-sort .MuiOutlinedInput-root {
                      border-radius: 10px;
                      background-color: #f8f9fc;
                      font-size: 0.875rem;
                      transition: background-color 0.2s ease, box-shadow 0.2s ease;
                    }

                    .campaign-report-search .MuiOutlinedInput-root:hover,
                    .campaign-report-sort .MuiOutlinedInput-root:hover {
                      background-color: #ffffff;
                    }

                    .campaign-report-search .MuiOutlinedInput-root.Mui-focused,
                    .campaign-report-sort .MuiOutlinedInput-root.Mui-focused {
                      background-color: #ffffff;
                      box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.14);
                    }

                    .campaign-report-search .MuiOutlinedInput-notchedOutline,
                    .campaign-report-sort .MuiOutlinedInput-notchedOutline {
                      border-color: #dce1ec;
                    }

                    .campaign-report-search .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline,
                    .campaign-report-sort .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
                      border-color: #b8c2d6;
                    }

                    .campaign-report-search .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline,
                    .campaign-report-sort .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
                      border-color: #1a73e8;
                      border-width: 1px;
                    }

                    .campaign-report-search .MuiInputAdornment-root {
                      color: #7b879c;
                    }

                    .campaign-report-sort {
                      width: 100%;
                    }

                    @media (min-width: 600px) {
                      .campaign-report-sort {
                        width: 260px;
                        flex-shrink: 0;
                      }
                    }

                    .campaign-report-sort .MuiInputLabel-root {
                      font-size: 0.875rem;
                      color: #5f6b7c;
                    }

                    .campaign-report-sort .MuiInputLabel-root.Mui-focused {
                      color: #1a73e8;
                    }

                    .campaign-report-sort .MuiSelect-select {
                      padding-top: 10px;
                      padding-bottom: 10px;
                      color: #344054;
                      font-weight: 500;
                    }

                    .campaign-report-sort .MuiSvgIcon-root {
                      color: #7b879c;
                    }

                    .campaign-report-table-scroll {
                      height: 428px;
                      overflow-y: auto;
                    }

                    .campaign-report-table-scroll::-webkit-scrollbar {
                      width: 6px;
                    }

                    .campaign-report-table-scroll::-webkit-scrollbar-thumb {
                      background-color: #c5cdd9;
                      border-radius: 6px;
                    }

                    .campaign-report-table-scroll::-webkit-scrollbar-track {
                      background-color: transparent;
                    }
                  `}</style>
                  <div className="campaign-report-toolbar">
                    <TextField
                      className="campaign-report-search"
                      placeholder="Search by Department"
                      variant="outlined"
                      size="small"
                      value={departmentSearchTerm}
                      onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <FormControl
                      className="campaign-report-sort"
                      variant="outlined"
                      size="small"
                    >
                      <InputLabel id="campaign-report-sort-label">Sort By</InputLabel>
                      <Select
                        labelId="campaign-report-sort-label"
                        value={sortOption}
                        label="Sort By"
                        onChange={(e) => setSortOption(e.target.value)}
                      >
                        <MenuItem value="department_asc">Department (A-Z)</MenuItem>
                        <MenuItem value="department_desc">Department (Z-A)</MenuItem>
                        <MenuItem value="applications_desc">Applications (High to Low)</MenuItem>
                        <MenuItem value="applications_asc">Applications (Low to High)</MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                </MDBox>
              </MDBox>
              <div className="campaign-report-table-scroll">
                {isCampaignSelected ? (
                  <DataTable
                    table={{ columns, rows }}
                    showTotalEntries={false}
                    isSorted={false}
                    noEndBorder
                    entriesPerPage={false}
                  />
                ) : (
                  <div style={{ textAlign: "center" }}>
                    <img
                      src={nodata}
                      alt="No Data"
                      style={{
                        display: "block",
                        margin: "auto",
                        width: "20%",
                        height: "20%",
                      }}
                    />
                  </div>
                )}
              </div>
            </Card>
          </Grid>
          </Grid>
        </MDBox>
        )}
        {isCampaignSelected && (
          <MDBox mt={userDepartment == null ? 3 : 0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12}>
                <Card className="dashboard-table">
                  <MDBox px={3} pb={3} pt={3}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={1}
                      mb={1}
                    >
                      <MDTypography variant="h6" gutterBottom style={{ marginTop: "8px" }}>
                        Top 25 Programs by Applications (Morning)
                        {selectedCampaignName ? ` - ${selectedCampaignName}` : ""}
                      </MDTypography>
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        style={{ color: "white" }}
                        onClick={handleViewAllPrograms}
                      >
                        View All Programs
                      </Button>
                    </MDBox>
                    {topProgramsReport.length > 0 ? (
                      <MDBox style={{ width: "100%", textAlign: "left" }}>
                        <HorizontalBarChart
                          icon={{ color: "info", component: "" }}
                          title=""
                          description=""
                          height={topProgramsChartHeight}
                          chart={topProgramsChartData}
                          chartOptions={topProgramsChartOptions}
                          plugins={[topProgramsYAxisLabelsPlugin]}
                        />
                      </MDBox>
                    ) : (
                      <div style={{ textAlign: "center", padding: "32px 0" }}>
                        <img
                          src={nodata}
                          alt="No Data"
                          style={{
                            display: "block",
                            margin: "auto",
                            width: "18%",
                            height: "auto",
                          }}
                        />
                        <MDTypography variant="body2" color="text" style={{ marginTop: "12px" }}>
                          No program application data available for this campaign.
                        </MDTypography>
                      </div>
                    )}
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;